/**
 * Etiket Yazdırma modülünün ana ekranı.
 *
 * İki görünüm: şablon listesi ve tasarımcı. Tasarımcı açıkken tüm alanı
 * kaplar (fiziksel ölçüde çalışırken ekran alanı değerli).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LabelIcon from '@mui/icons-material/Label';
import { useAuth } from '../../contexts/AuthContext';
import labelTemplateService from '../../services/labelTemplateService';
import { LabelTemplate, getDesignSize } from '../../types/labelTemplate.types';
import { LABEL_PRESETS, buildPreset } from './labelTemplatePresets';
import LabelDesigner from './designer/LabelDesigner';
import LabelPrintDialog from './print/LabelPrintDialog';
import LabelHtmlRenderer from './render/LabelHtmlRenderer';
import GuideButton from '../common/guide/GuideButton';
import { SAMPLE_INVENTORY_PLACEHOLDERS, SAMPLE_WMS_PLACEHOLDERS } from './dataSources/inventoryLabelSource';

const LabelPrintingPage: React.FC = () => {
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId ?? '';

  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [designing, setDesigning] = useState<LabelTemplate | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [printTemplateId, setPrintTemplateId] = useState<string | undefined>();
  const [presetOpen, setPresetOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; template: LabelTemplate } | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await labelTemplateService.list(companyId));
    } catch {
      setToast({ msg: 'Şablonlar yüklenemedi', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * Tasarımcı kaydedilen şablonu geri bekliyor: yeni kayıtta backend'in
   * ürettiği kimliği alıp sonraki kaydetmelerde aynı kaydı güncelliyor.
   */
  const handleSave = useCallback(
    async (template: LabelTemplate): Promise<LabelTemplate> => {
      const saved = await labelTemplateService.save(companyId, template);
      await reload();
      return saved;
    },
    [companyId, reload],
  );

  const handleDelete = useCallback(
    async (template: LabelTemplate) => {
      if (!template.id) return;
      // eslint-disable-next-line no-restricted-globals
      if (!window.confirm(`"${template.name}" şablonu silinsin mi?`)) return;
      await labelTemplateService.remove(companyId, template.id);
      setToast({ msg: 'Şablon silindi', severity: 'success' });
      await reload();
    },
    [companyId, reload],
  );

  const handleDuplicate = useCallback(
    async (template: LabelTemplate) => {
      if (!template.id) return;
      await labelTemplateService.duplicate(companyId, template.id);
      setToast({ msg: 'Şablon kopyalandı', severity: 'success' });
      await reload();
    },
    [companyId, reload],
  );

  const handleSetDefault = useCallback(
    async (template: LabelTemplate) => {
      if (!template.id) return;
      await labelTemplateService.setDefault(companyId, template.id);
      await reload();
    },
    [companyId, reload],
  );

  const handleExport = (template: LabelTemplate) => {
    const blob = new Blob([labelTemplateService.exportToJson(template)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name.replace(/\s+/g, '-').toLowerCase()}.etiket.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (file: File) => {
    try {
      const text = await file.text();
      const imported = labelTemplateService.importFromJson(text);
      await labelTemplateService.save(companyId, imported);
      setToast({ msg: 'Şablon içe aktarıldı', severity: 'success' });
      await reload();
    } catch (err) {
      setToast({ msg: err instanceof Error ? err.message : 'İçe aktarma başarısız', severity: 'error' });
    }
  };

  const openPrint = (templateId?: string) => {
    setPrintTemplateId(templateId);
    setPrintOpen(true);
  };

  /**
   * Tasarımcıdan yazdırma. Yazdırma dialogu şablonu kayıtlı listeden okuduğu
   * için tasarım HER SEFERİNDE önce kaydedilir — yalnızca yeni şablonu
   * kaydetmek yetmiyor: mevcut şablona eklenen bir eleman kaydedilmeden
   * yazdırılırsa ekranda görünen ile basılan ayrışıyor (eleman baskıda yok).
   */
  const handleDesignerPrint = useCallback(
    async (template: LabelTemplate): Promise<LabelTemplate | null> => {
      try {
        const saved = await labelTemplateService.save(companyId, template);
        await reload();
        openPrint(saved.id);
        return saved;
      } catch (err) {
        setToast({
          msg: err instanceof Error ? err.message : 'Şablon kaydedilemedi',
          severity: 'error',
        });
        return null;
      }
    },
    [companyId, reload],
  );

  /** Kart üzerindeki küçük önizleme, karta sığacak şekilde ölçeklenir */
  const cardPreviewScale = useCallback((t: LabelTemplate) => {
    const maxWidthPx = 240;
    const widthPx = (t.labelWidthMm * 96) / 25.4;
    return Math.min(1, maxWidthPx / widthPx);
  }, []);

  const sampleFor = (t: LabelTemplate) =>
    t.dataSource === 'wmsLocation' ? SAMPLE_WMS_PLACEHOLDERS : SAMPLE_INVENTORY_PLACEHOLDERS;

  // Tasarımcı ve liste ekranı ayrı dallarda döndüğü için ortak bildirim
  const toastNode = (
    <Snackbar
      open={!!toast}
      autoHideDuration={4000}
      onClose={() => setToast(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity={toast?.severity ?? 'success'} onClose={() => setToast(null)}>
        {toast?.msg}
      </Alert>
    </Snackbar>
  );

  if (designing) {
    return (
      <Box sx={{ height: 'calc(100vh - 64px)' }}>
        <LabelDesigner
          initialTemplate={designing}
          onSave={handleSave}
          onPrint={handleDesignerPrint}
          onClose={() => setDesigning(null)}
        />

        <LabelPrintDialog
          open={printOpen}
          onClose={() => setPrintOpen(false)}
          templates={templates}
          initialTemplateId={printTemplateId}
        />

        {toastNode}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <LabelIcon color="primary" sx={{ fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="h5" fontWeight={600}>
              Etiket Yazdırma
            </Typography>
            <GuideButton guideKey="inventory.labelPrinting" size="small" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Etiket şablonlarınızı tasarlayın, ürünleri seçip toplu etiket bastırın.
          </Typography>
        </Box>

        <Button component="label" variant="outlined" startIcon={<FileUploadIcon />}>
          İçe Aktar
          <input
            type="file"
            hidden
            accept=".json"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleImport(f);
              e.target.value = '';
            }}
          />
        </Button>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setPresetOpen(true)}>
          Yeni Şablon
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => openPrint(undefined)}
          disabled={templates.length === 0}
        >
          Etiket Yazdır
        </Button>
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      ) : templates.length === 0 ? (
        <Alert
          severity="info"
          action={
            <Button size="small" onClick={() => setPresetOpen(true)}>
              Şablon Oluştur
            </Button>
          }
        >
          Henüz etiket şablonu yok. Hazır bir şablonla başlayıp kendinize göre düzenleyebilirsiniz.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {templates.map((t) => {
            const design = getDesignSize(t);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
                <Card variant="outlined" sx={{ height: '100%', position: 'relative' }}>
                  <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, display: 'flex' }}>
                    <Tooltip title={t.isDefault ? 'Varsayılan şablon' : 'Varsayılan yap'}>
                      <IconButton size="small" onClick={() => handleSetDefault(t)}>
                        {t.isDefault ? (
                          <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        ) : (
                          <StarBorderIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, template: t })}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <CardActionArea onClick={() => setDesigning(t)}>
                    {/* Gerçek oranlarda küçük önizleme */}
                    <Box
                      sx={{
                        height: 150,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ transform: `scale(${cardPreviewScale(t)})`, transformOrigin: 'center' }}>
                        <LabelHtmlRenderer template={t} data={sampleFor(t)} showBounds />
                      </Box>
                    </Box>

                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="subtitle2" noWrap title={t.name}>
                        {t.name}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip size="small" label={`${t.labelWidthMm}×${t.labelLengthMm} mm`} />
                        {t.rotation !== 0 && <Chip size="small" color="info" label={`${t.rotation}°`} />}
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t.layout.mode === 'roll' ? 'Rulo' : `${t.layout.columns}×${t.layout.rows}`}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        {t.elements.length} eleman · tasarım {design.widthMm}×{design.heightMm} mm
                      </Typography>
                    </CardContent>
                  </CardActionArea>

                  <Divider />
                  <Stack direction="row" spacing={1} sx={{ p: 1 }}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setDesigning(t)} sx={{ flex: 1 }}>
                      Düzenle
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PrintIcon />}
                      onClick={() => openPrint(t.id)}
                      sx={{ flex: 1 }}
                    >
                      Bas
                    </Button>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Kart menüsü */}
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            if (menuAnchor) setDesigning(menuAnchor.template);
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Düzenle</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor) handleDuplicate(menuAnchor.template);
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Kopyala</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor) handleExport(menuAnchor.template);
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dışa Aktar</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuAnchor) handleDelete(menuAnchor.template);
            setMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Sil</ListItemText>
        </MenuItem>
      </Menu>

      {/* Hazır şablon seçimi */}
      <Dialog open={presetOpen} onClose={() => setPresetOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Yeni Etiket Şablonu</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Hazır bir düzenle başlayın; ölçüleri ve içeriği sonra değiştirebilirsiniz.
          </Typography>
          <Grid container spacing={2}>
            {LABEL_PRESETS.map((preset) => (
              <Grid item xs={12} sm={6} key={preset.id}>
                <Card variant="outlined">
                  <CardActionArea
                    onClick={() => {
                      setDesigning(buildPreset(preset.id));
                      setPresetOpen(false);
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle2">{preset.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {preset.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      <LabelPrintDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        templates={templates}
        initialTemplateId={printTemplateId}
      />

      {toastNode}
    </Box>
  );
};

export default LabelPrintingPage;
